import { useState, useEffect } from 'react';
import { 
  UserPlus, 
  UserCheck, 
  ArrowLeft, 
  Award, 
  Calendar, 
  Zap, 
  Play, 
  ChevronDown, 
  ChevronUp,
  Loader2,
  Flame,
  BookOpen,
  Sparkles,
  Trash2,
  Check,
  Star
} from 'lucide-react';
import { normalizeAjaxUrl } from '@/utils/normalizeUrl';
import {
  compulsorySubjects,
  scienceSubjects,
  humanitiesSubjects,
  commerceSubjects,
  optionalSubjects
} from '../../data/syllabus';

interface ProfileCompareViewProps {
  targetUserId: number;
  onClose?: () => void;
  isDarkMode?: boolean;
  onChallengeFriend?: (friendId: number, friendName: string) => void;
}

const allSyllabusSubjects = [
  ...compulsorySubjects,
  ...scienceSubjects,
  ...humanitiesSubjects,
  ...commerceSubjects,
  ...Object.values(optionalSubjects)
];

const getChaptersForSubjectName = (subjName: string) => {
  const normalizedSearch = subjName.replace(/য়/g, 'য়').toLowerCase().trim();
  const matchedSub = allSyllabusSubjects.find(s => {
    const normalizedSyllable = s.name.replace(/য়/g, 'য়').toLowerCase().trim();
    return normalizedSyllable === normalizedSearch || 
           normalizedSyllable.includes(normalizedSearch) || 
           normalizedSearch.includes(normalizedSyllable);
  });
  return matchedSub ? matchedSub.chapters : [];
};

export default function ProfileCompareView({ 
  targetUserId, 
  onClose, 
  isDarkMode,
  onChallengeFriend 
}: ProfileCompareViewProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any | null>(null);
  const [socialLoading, setSocialLoading] = useState(false);
  const [expandedSubjectIdx, setExpandedSubjectIdx] = useState<number | null>(null);
  const [selectedSubjectDetail, setSelectedSubjectDetail] = useState<any | null>(null);
  const [aiExplanationOpenIdx, setAiExplanationOpenIdx] = useState<number | null>(null);
  const [wrongQuestionFilter, setWrongQuestionFilter] = useState<string>('all');
  const [removedWrongQuestionIds, setRemovedWrongQuestionIds] = useState<number[]>([]);

  const shAuth = (window as any).shAuth;
  const reactObj = (window as any).sh_react_object;
  const ajaxUrl = normalizeAjaxUrl(shAuth?.ajax_url || reactObj?.ajax_url || '/wp-admin/admin-ajax.php');
  const nonce = reactObj?.nonce || shAuth?.nonce || '';

  const fetchData = async () => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('action', 'sh_get_compare_profile_data');
      formData.append('target_user_id', String(targetUserId));
      formData.append('nonce', nonce);

      const response = await fetch(ajaxUrl, {
        method: 'POST',
        body: formData,
      });

      const res = await response.json();
      if (res.success) {
        setData(res.data);
      } else {
        const errMsg = typeof res.data === 'string' ? res.data : (res.data?.message || 'প্রোফাইল লোড করতে ব্যর্থ হয়েছে।');
        setError(errMsg);
      }
    } catch (err) {
      setError('একটি নেটওয়ার্ক ত্রুটি ঘটেছে।');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [targetUserId]);

  const handleSocialAction = async () => {
    if (!data?.target) return;
    const relation = data.target.relation;
    let action = '';

    if (relation === 'none') {
      action = 'sh_send_friend_request';
    } else if (relation === 'pending_received') {
      action = 'sh_accept_friend_request';
    } else if (relation === 'pending_sent') {
      if (!confirm('আপনি কি সত্যিই এই অনুরোধটি প্রত্যাহার করতে চান?')) return;
      action = 'sh_decline_friend_request';
    } else if (relation === 'friend') {
      if (!confirm('আপনি কি সত্যিই এই বন্ধুকে তালিকা থেকে বাদ দিতে চান?')) return;
      action = 'sh_unfriend';
    } else {
      return;
    }

    try {
      setSocialLoading(true);
      const formData = new FormData();
      formData.append('action', action);
      formData.append('target_user_id', String(targetUserId));
      formData.append('nonce', nonce);

      const response = await fetch(ajaxUrl, {
        method: 'POST',
        body: formData,
      });

      const res = await response.json();
      if (res.success) {
        // Refresh data
        await fetchData();
      } else {
        const msg = typeof res.data === 'string' ? res.data : (res.data?.message || 'সামাজিক সংযোগ আপডেট ব্যর্থ হয়েছে।');
        alert(msg);
      }
    } catch {
      alert('নেটওয়ার্ক সংযোগ ব্যর্থ হয়েছে।');
    } finally {
      setSocialLoading(false);
    }
  };

  // Convert numbers to Bengali digits
  const toBnNum = (num: any): string => {
    if (num === null || num === undefined) return '০';
    const enToBn: Record<string, string> = {
      '0': '০', '1': '১', '2': '২', '3': '৩', '4': '৪',
      '5': '৫', '6': '৬', '7': '৭', '8': '৮', '9': '৯'
    };
    return String(num).split('').map(digit => enToBn[digit] || digit).join('');
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse text-left">
        {/* Header/Profile Skeleton */}
        <div className="flex flex-col sm:flex-row items-center gap-5 p-4 bg-slate-50 dark:bg-zinc-900/40 rounded-3xl border border-slate-100 dark:border-zinc-800/80">
          <div className="w-20 h-20 rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="flex-1 space-y-3">
            <div className="w-32 h-6 bg-slate-200 dark:bg-slate-800 rounded-xl" />
            <div className="w-48 h-4 bg-slate-100 dark:bg-slate-800/50 rounded-lg" />
            <div className="flex gap-2 pt-1">
              <div className="w-16 h-5 bg-slate-100 dark:bg-slate-800/50 rounded-full" />
              <div className="w-16 h-5 bg-slate-100 dark:bg-slate-800/50 rounded-full" />
            </div>
          </div>
        </div>
        {/* 4 Stats Grid Skeleton */}
        <div className="grid grid-cols-4 gap-4 p-4">
          {[1, 2, 3, 4].map(n => (
            <div key={n} className="space-y-2 text-center">
              <div className="w-12 h-3 bg-slate-100 dark:bg-slate-800/50 rounded-md mx-auto" />
              <div className="w-8 h-5 bg-slate-200 dark:bg-slate-800 rounded-md mx-auto" />
            </div>
          ))}
        </div>
        {/* Row of Charts Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="h-44 bg-slate-50 dark:bg-zinc-900/40 border border-slate-100 dark:border-zinc-800/80 rounded-3xl p-5" />
          <div className="h-44 bg-slate-50 dark:bg-zinc-900/40 border border-slate-100 dark:border-zinc-800/80 rounded-3xl p-5" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6 text-center text-rose-500 dark:text-rose-450 border border-rose-250/15 rounded-2xl bg-rose-50/5">
        <p className="font-bangla text-sm mb-4">{error || 'কোনো তথ্য পাওয়া যায়নি'}</p>
        <button
          onClick={onClose}
          className="px-6 py-2.5 rounded-xl bg-gray-200 dark:bg-white/10 text-slate-800 dark:text-white font-bangla text-xs font-bold border-none cursor-pointer"
        >
          ফিরে যান
        </button>
      </div>
    );
  }

  const target = data?.target || { id: 0, name: 'শিক্ষার্থী', class: '', institution: '', avatar: '', relation: 'none', points: 0, rank: '০', exams: 0, streak: 0 };
  const isSelf = Number(targetUserId) === Number(shAuth?.user?.id) || Number(target.id) === Number(shAuth?.user?.id) || !targetUserId;
  const targetName = target.name || 'শিক্ষার্থী';
  const subjects = Array.isArray(data?.subjects) ? data.subjects : [];
  const hist = data?.history || { days: [], target: [], current: [] };
  const daysList = Array.isArray(hist.days) ? hist.days : [];
  const targetHistory = Array.isArray(hist.target) ? hist.target : [];
  const currentHistory = Array.isArray(hist.current) ? hist.current : [];

  const displayPoints = isSelf ? (shAuth?.user?.points ?? target.points) : target.points;
  const displayExams = isSelf ? (shAuth?.user?.exams ?? target.exams) : target.exams;
  const displayStreak = isSelf ? (shAuth?.user?.streak ?? target.streak) : target.streak;
  const displayRank = isSelf ? (shAuth?.user?.rank ?? target.rank) : target.rank;

  // Render points comparison chart variables
  const maxVal = Math.max(...targetHistory, ...currentHistory, 100);
  const chartHeight = 150;
  const chartWidth = 300;
  const padding = 20;

  const getCoordinates = (values: number[]) => {
    return values.map((val, idx) => {
      const x = padding + (idx * (chartWidth - 2 * padding)) / 6;
      const y = chartHeight - padding - (val * (chartHeight - 2 * padding)) / maxVal;
      return { x, y };
    });
  };

  const targetCoords = getCoordinates(targetHistory);
  const currentCoords = getCoordinates(currentHistory);

  const getDPath = (coords: { x: number; y: number }[]) => {
    return coords.reduce((path, coord, idx) => {
      return path + (idx === 0 ? `M ${coord.x} ${coord.y}` : ` L ${coord.x} ${coord.y}`);
    }, '');
  };

  const getButtonStyle = () => {
    if (socialLoading) {
      return isDarkMode 
        ? { backgroundColor: '#1e293b', color: '#64748b' }
        : { backgroundColor: '#f1f5f9', color: '#94a3b8' };
    }
    if (target.relation === 'friend') {
      return { backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' };
    }
    if (target.relation === 'pending_sent') {
      return { backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' };
    }
    if (target.relation === 'pending_received') {
      return { backgroundColor: '#10b981', color: '#ffffff' };
    }
    // relation === 'none'
    return isDarkMode
      ? { backgroundColor: '#ffffff', color: '#0f172a' }
      : { backgroundColor: '#0f172a', color: '#ffffff' };
  };
  if (selectedSubjectDetail) {
    const isBengali = selectedSubjectDetail.name.includes('বাংলা');
    const chaps = getChaptersForSubjectName(selectedSubjectDetail.name);
    const chapters = chaps && chaps.length > 0
      ? chaps.map((chap: any, cIdx: number) => {
          const simulatedProgress = selectedSubjectDetail.target_progress > 0 
            ? Math.min(100, Math.max(0, Math.round(selectedSubjectDetail.target_progress * (1.1 - cIdx * 0.15))))
            : 0;
          return { name: chap.name, progress: simulatedProgress };
        })
      : [
          { name: "অধ্যায় ১: পরিচিতি", progress: 5 },
          { name: "অধ্যায় ২: মূল ধারণা", progress: 3 }
        ];

    const allWrongQuestions = isBengali ? [
      { 
        id: 1, 
        text: "১. 'আমি কি বকরি-ঈদের গরু ছাগল না কী?' এখানে তাহেরার কোন মনোভাব প্রকাশ পেয়েছে?", 
        options: ["ক. i ও ii", "খ. i ও iii", "গ. ii ও iii", "ঘ. i, ii ও iii"], 
        correct: 0, 
        tag: "DB 22", 
        type: "গদ্য", 
        explanation: "এখানে তাহেরার চরিত্রের অনমনীয় প্রতিবাদ ও ব্যক্তিত্বের দৃঢ়তা প্রকাশ পেয়েছে। সে নিজেকে কোনো অবলা প্রাণী বা কেনাবেচার বস্তু মনে করে না, বরং নিজের অধিকার ও সিদ্ধান্তের পক্ষে লড়াই করতে প্রস্তুত।" 
      },
      { 
        id: 2, 
        text: "২. 'সুভা' গল্পে সুভার পিতার নাম কী ছিল?", 
        options: ["ক. বাণীকণ্ঠ", "খ. হরকণ্ঠ", "গ. শিবকণ্ঠ", "ঘ. রামকণ্ঠ"], 
        correct: 0, 
        tag: "JB 24", 
        type: "গদ্য", 
        explanation: "রবীন্দ্রনাথ ঠাকুরের বিখ্যাত 'সুভা' গল্পে সুভার বাবার নাম ছিল বাণীকণ্ঠ। তিনি সুভাকে অন্য সন্তানদের চেয়ে একটু বেশি ভালোবাসতেন।" 
      },
      { 
        id: 3, 
        text: "৩. 'বঙ্গবাণী' কবিতায় কবি কাদের দেশ ছাড়তে বলেছেন?", 
        options: ["ক. যারা বাংলা ভাষাকে হিংসা করে", "খ. যারা বাংলা ভাষায় কথা বলে না", "গ. যারা মূর্খ", "ঘ. যারা অলস"], 
        correct: 0, 
        tag: "CB 23", 
        type: "কবিতা", 
        explanation: "কবি আবদুল হাকিম তাঁর 'বঙ্গবাণী' কবিতায় তীব্র ক্ষোভ নিয়ে ব্যক্ত করেছেন যে, যারা বাংলায় জন্মগ্রহণ করেও বাংলা ভাষাকে হিংসা করে, তাদের এদেশ ছেড়ে অন্য দেশে চলে যাওয়া উচিত।" 
      },
      { 
        id: 4, 
        text: "৪. 'কাকতাড়ুয়া' উপন্যাসের পটভূমি কী?", 
        options: ["ক. বাংলাদেশের মুক্তিযুদ্ধ", "খ. বায়ান্নর ভাষা আন্দোলন", "গ. ছিয়াত্তরের মন্বন্তর", "ঘ. তেভাগা আন্দোলন"], 
        correct: 0, 
        tag: "RB 21", 
        type: "সহপাঠ", 
        explanation: "সেলিনা হোসেনের বিখ্যাত কিশোর উপন্যাস 'কাকতাড়ুয়া'-র পটভূমি হলো ১৯৭১ সালের বাংলাদেশের মহান মুক্তিযুদ্ধ। এর প্রধান চরিত্র বুধা একজন অনাথ ও সাহসী কিশোর মুক্তিযোদ্ধা।" 
      }
    ] : [
      { 
        id: 10, 
        text: "1. Which of the following is correct about noun clauses?", 
        options: ["A. They function as subjects or objects", "B. They modify nouns", "C. They modify verbs", "D. They show time"], 
        correct: 0, 
        tag: "Board 22", 
        type: "অধ্যায় ১", 
        explanation: "A noun clause functions as a noun in a sentence, meaning it can act as a subject, direct object, indirect object, predicate nominative, or object of a preposition." 
      },
      { 
        id: 11, 
        text: "2. Identify the correct sentence:", 
        options: ["A. He has been reading since three hours", "B. He has been reading for three hours", "C. He is reading since three hours", "D. He was reading for three hours"], 
        correct: 1, 
        tag: "Board 23", 
        type: "অধ্যায় ২", 
        explanation: "We use 'for' to describe a duration of time (e.g. three hours) and 'since' to describe a specific starting point in time." 
      }
    ];

    const subjectWrongQuestions = allWrongQuestions.filter(q => !removedWrongQuestionIds.includes(q.id));
    const filteredQuestions = wrongQuestionFilter === 'all' 
      ? subjectWrongQuestions 
      : subjectWrongQuestions.filter(q => q.type === wrongQuestionFilter);

    const filterTabs = ['সব', ...Array.from(new Set(subjectWrongQuestions.map(q => q.type)))];

    const circ = 2 * Math.PI * 30; // 188.4
    const correctLength = (11 / 25) * circ;
    const wrongLength = (14 / 25) * circ;

    return (
      <div className="space-y-6">
        {/* Back Header for Subject Detail */}
        <div className="flex items-center justify-between border-b border-gray-150 dark:border-zinc-800/80 pb-4 mb-2">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => {
                setSelectedSubjectDetail(null);
                setAiExplanationOpenIdx(null);
                setWrongQuestionFilter('all');
              }}
              className={`p-2 rounded-xl transition-colors border-none cursor-pointer ${isDarkMode ? 'bg-white/5 hover:bg-white/10 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h4 className="font-bangla text-sm font-bold text-slate-800 dark:text-white flex items-center gap-1.5">
              <span className="text-gray-400 dark:text-zinc-500 font-medium">প্রোগ্রেস</span>
              <span className="text-gray-300 dark:text-zinc-700">/</span>
              <span>{selectedSubjectDetail.name}</span>
            </h4>
          </div>

          {/* Stats badges */}
          <div className="flex items-center gap-2">
            <span className="font-bangla text-[10px] font-bold px-3 py-1 bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 rounded-full flex items-center gap-1">
              <Zap className="w-3.5 h-3.5 fill-yellow-500 text-transparent animate-pulse" />
              {toBnNum(0)}
            </span>
            <span className="font-bangla text-[10px] font-bold px-3 py-1 bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 rounded-full flex items-center gap-1">
              <Star className="w-3.5 h-3.5 fill-yellow-500 text-yellow-500" />
              {toBnNum(0)} পয়েন্ট
            </span>
          </div>
        </div>

        {/* Two column detail page */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* Left Column (spans 5 cols) */}
          <div className="lg:col-span-5 space-y-6">
            
            {/* Accuracy donut card */}
            <div className="bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md rounded-3xl p-6 border border-gray-100 dark:border-white/5 shadow-sm space-y-5 flex items-center gap-6">
              {/* Donut chart SVG */}
              <div className="relative w-20 h-20 flex items-center justify-center flex-shrink-0">
                <svg className="w-full h-full" viewBox="0 0 80 80">
                  <circle
                    cx="40"
                    cy="40"
                    r="30"
                    stroke="#10b981"
                    strokeWidth="8"
                    fill="transparent"
                    strokeDasharray={`${correctLength} ${circ}`}
                    strokeDashoffset="0"
                    className="transform rotate-[-90deg] origin-[40px_40px]"
                  />
                  <circle
                    cx="40"
                    cy="40"
                    r="30"
                    stroke="#ef4444"
                    strokeWidth="8"
                    fill="transparent"
                    strokeDasharray={`${wrongLength} ${circ}`}
                    strokeDashoffset={-correctLength}
                    className="transform rotate-[-90deg] origin-[40px_40px]"
                  />
                </svg>
                <div className="absolute flex flex-col items-center justify-center">
                  <span className="font-english text-sm font-black text-slate-800 dark:text-white leading-none">৪৪%</span>
                </div>
              </div>

              {/* Accuracy stats legend */}
              <div className="flex-1 space-y-2">
                <h4 className="font-bangla text-xs font-bold text-slate-800 dark:text-white mb-2">অ্যাকুরিসি</h4>
                <div className="flex flex-col gap-1.5 text-[10px] font-bangla font-semibold text-slate-650 dark:text-slate-400">
                  <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#10b981]"></span>{toBnNum(11)} সঠিক</span>
                  <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#ef4444]"></span>{toBnNum(14)} ভুল</span>
                  <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#f59e0b]"></span>{toBnNum(0)} স্কিপড</span>
                </div>
              </div>
            </div>

            {/* Total Attempted bar card */}
            <div className="bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md rounded-3xl p-6 border border-gray-100 dark:border-white/5 shadow-sm space-y-3">
              <div className="flex items-center justify-between text-xs font-bangla font-semibold">
                <span className="text-gray-400 dark:text-zinc-550">মোট এটেম্পটেড প্রশ্ন</span>
                <span className="text-slate-800 dark:text-white">{toBnNum(25)}/{toBnNum('৬.৭K')} টি প্রশ্ন</span>
              </div>
              <div className="h-3 bg-gray-100 dark:bg-white/5 rounded-full overflow-hidden flex items-center relative">
                <div className="h-full bg-[#00CBA9] rounded-full" style={{ width: '1%' }} />
                <span className="absolute right-3 text-[9px] font-english font-black text-slate-700 dark:text-gray-300">0.4%</span>
              </div>
            </div>

            {/* Topic/Chapter Accordion list */}
            <div className="space-y-3">
              {chapters.map((chap: any, idx: number) => (
                <div key={idx} className="bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md rounded-2xl p-4.5 border border-gray-100 dark:border-white/5 shadow-sm hover:border-[#00CBA9]/30 transition-all duration-300">
                  <div className="flex items-center justify-between text-xs font-bangla font-semibold cursor-pointer">
                    <span className="text-slate-850 dark:text-white">{chap.name}</span>
                    <span className="text-[#00CBA9] flex items-center gap-1.5">
                      {toBnNum(chap.progress)}%
                      <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                    </span>
                  </div>
                </div>
              ))}
            </div>

          </div>

          {/* Right Column (spans 7 cols) */}
          <div className="lg:col-span-7 bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md rounded-3xl p-6 border border-gray-100 dark:border-white/5 shadow-sm space-y-5">
            <div className="flex items-center justify-between border-b border-gray-50 dark:border-white/5 pb-3">
              <h4 className="font-bangla text-xs font-bold text-gray-700 dark:text-gray-300">ভুল প্রশ্ন</h4>
              
              {/* Filter pills */}
              <div className="flex gap-1.5 overflow-x-auto">
                {filterTabs.map(tab => (
                  <button
                    key={tab}
                    onClick={() => setWrongQuestionFilter(tab === 'সব' ? 'all' : tab)}
                    className={`px-3 py-1 rounded-full text-[10px] font-bangla font-bold border cursor-pointer transition-all ${
                      (tab === 'সব' && wrongQuestionFilter === 'all') || (wrongQuestionFilter === tab)
                        ? 'bg-slate-900 text-white dark:bg-[#8AB4F8] dark:text-[#121212] border-transparent'
                        : 'bg-slate-50 dark:bg-white/5 text-slate-500 dark:text-zinc-400 border-slate-100 dark:border-white/5'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            {/* Questions list */}
            <div className="space-y-4 max-h-[500px] overflow-y-auto pr-1">
              {filteredQuestions.map((q) => (
                <div key={q.id} className="bg-slate-50/50 dark:bg-zinc-900/30 border border-slate-100 dark:border-white/5 rounded-2xl p-5 space-y-4 text-left">
                  {/* Question text */}
                  <div className="text-xs font-bangla font-bold text-slate-800 dark:text-white leading-relaxed">
                    {q.text}
                  </div>
                  
                  {/* Options */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {q.options.map((opt, oIdx) => {
                      const isCorrect = oIdx === q.correct;
                      return (
                        <div 
                          key={oIdx}
                          className={`flex items-center gap-2.5 p-3 rounded-xl border text-xs font-bangla transition-all ${
                            isCorrect 
                              ? 'bg-emerald-500/10 border-emerald-500/35 text-emerald-600 dark:text-emerald-400 font-semibold' 
                              : 'bg-white dark:bg-zinc-900 border-slate-200/60 dark:border-zinc-800 text-slate-600 dark:text-slate-400'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center border text-[10px] font-black flex-shrink-0 ${
                            isCorrect 
                              ? 'bg-emerald-500 text-white border-transparent' 
                              : 'border-slate-300 dark:border-zinc-700 text-slate-400'
                          }`}>
                            {isCorrect ? <Check className="w-3 h-3" /> : (oIdx === 0 ? 'ক' : oIdx === 1 ? 'খ' : oIdx === 2 ? 'গ' : 'ঘ')}
                          </div>
                          <span>{opt}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Metadata and Action Row */}
                  <div className="flex items-center justify-between border-t border-slate-100 dark:border-zinc-800/80 pt-3 text-[10px] font-bangla font-bold">
                    <div className="flex items-center gap-2">
                      <span className="px-2.5 py-1 bg-sky-500/10 text-sky-500 rounded-lg">{q.tag}</span>
                      <span className="px-2.5 py-1 bg-rose-500/10 text-rose-500 rounded-lg">পূর্বের ভুল</span>
                    </div>
                    <button 
                      onClick={() => setRemovedWrongQuestionIds([...removedWrongQuestionIds, q.id])}
                      className="p-1.5 rounded-lg text-rose-450 hover:bg-rose-500/10 transition-colors border-none cursor-pointer bg-transparent"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* AI Explanation Dropdown */}
                  <div className="border border-emerald-500/10 dark:border-emerald-500/5 rounded-xl overflow-hidden bg-emerald-500/[0.02] dark:bg-emerald-500/[0.01]">
                    <button 
                      onClick={() => setAiExplanationOpenIdx(aiExplanationOpenIdx === q.id ? null : q.id)}
                      className="w-full flex items-center justify-between p-3.5 text-xs font-bangla font-extrabold text-[#00CBA9] dark:text-emerald-400 bg-transparent border-0 cursor-pointer"
                    >
                      <span className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 fill-[#00CBA9] dark:fill-emerald-400 text-transparent" />
                        AI ব্যাখ্যা
                      </span>
                      <span className="font-semibold text-gray-400 dark:text-zinc-550 flex items-center gap-1">
                        দৈনিক AI ব্যাখ্যা বাকি - ৪/৫
                        {aiExplanationOpenIdx === q.id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      </span>
                    </button>

                    {aiExplanationOpenIdx === q.id && (
                      <div className="px-4 pb-4 text-xs font-bangla text-slate-650 dark:text-slate-400 leading-relaxed border-t border-emerald-500/10 pt-3 animate-fadeIn">
                        {q.explanation}
                      </div>
                    )}
                  </div>

                </div>
              ))}
              {filteredQuestions.length === 0 && (
                <div className="space-y-3">
                  <div className="h-24 w-full bg-slate-100 dark:bg-white/5 rounded-2xl animate-pulse" />
                  <div className="h-24 w-full bg-slate-100 dark:bg-white/5 rounded-2xl animate-pulse" />
                </div>
              )}
            </div>

          </div>
        </div>

      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Header */}
      <div className="flex items-center gap-3">
        {onClose && (
          <button 
            onClick={onClose}
            className={`p-2 rounded-xl transition-colors border-none cursor-pointer ${isDarkMode ? 'bg-white/5 hover:bg-white/10 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}
            style={isDarkMode ? { backgroundColor: 'rgba(255, 255, 255, 0.05)', color: '#ffffff' } : { backgroundColor: '#f1f5f9', color: '#334155' }}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}
        <h4 className="font-bangla text-base font-extrabold text-[#1F1F1F] dark:text-[#E8EAED]">{isSelf ? 'আমার প্রোগ্রেস' : 'প্রোফাইল তুলনা'}</h4>
      </div>

      {/* 1. Target User Main Profile Card */}
      <div className={`relative overflow-hidden rounded-3xl p-6 shadow-xl transition-all duration-300 border ${
        isDarkMode 
          ? 'bg-slate-900 text-white border-white/5 bg-gradient-to-b from-[#1F2633] to-[#121620]' 
          : 'bg-[#F4F2FF] text-slate-900 border-[#E5DEFF] shadow-sm'
      }`}>
        {/* Banner Decorative */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#00CBA9] to-[#00A3FF] opacity-10 rounded-bl-full pointer-events-none" />

        <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-6 relative z-10">
          <div className="flex flex-col md:flex-row items-center gap-5 text-center md:text-left">
            {/* Avatar */}
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#00CBA9] to-[#00A3FF] border-2 border-emerald-400/80 flex items-center justify-center text-white font-bangla font-black text-2xl shadow-md overflow-hidden">
              {(() => {
                const avatarUrl = isSelf ? (shAuth?.user?.avatar || target.avatar) : target.avatar;
                const dispName = isSelf ? (shAuth?.user?.name || target.name || 'শিক্ষার্থী') : targetName;
                return avatarUrl ? (
                  <img src={avatarUrl} alt={dispName} className="w-full h-full object-cover" />
                ) : (
                  dispName.slice(0, 2)
                );
              })()}
            </div>

            <div className="space-y-1">
              <h3 className="font-bangla text-lg font-extrabold">
                {isSelf ? (shAuth?.user?.name || target.name || 'শিক্ষার্থী') : targetName}
              </h3>
              <p className={`font-bangla text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-[#5B4E99]'}`}>
                {(() => {
                  const rawClass = isSelf ? (shAuth?.user?.class || target.class || '') : (target.class || '');
                  if (rawClass.toLowerCase().startsWith('hsc') || rawClass.toLowerCase() === 'hsc_2026' || rawClass.toLowerCase() === 'hsc-2026') {
                    return 'Batch : HSC 2027';
                  }
                  return 'Batch : SSC 2027';
                })()} from {isSelf ? (shAuth?.user?.institution || target.institution || 'Burail Model School And College') : (target.institution || 'Burail Model School And College')}
              </p>
              
              {/* Target Badges mockup header row */}
              <div className="flex items-center justify-center md:justify-start gap-2 pt-2">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bangla font-bold ${
                  isDarkMode 
                    ? 'bg-[#00C9A7]/10 border border-[#00C9A7]/20 text-[#00E0BA]' 
                    : 'bg-[#00C9A7]/15 border border-[#00C9A7]/25 text-[#007A65]'
                }`} title="সঠিক উত্তর">
                  <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                  <span>সঠিক উত্তর</span>
                </span>
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bangla font-bold ${
                  isDarkMode 
                    ? 'bg-[#00C9A7]/10 border border-[#00C9A7]/20 text-[#00E0BA]' 
                    : 'bg-[#00C9A7]/15 border border-[#00C9A7]/25 text-[#007A65]'
                }`} title="মোট পরীক্ষা">
                  <Zap className="w-3 h-3 text-[#00C9A7]" />
                  <span>পরীক্ষা</span>
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bangla font-bold bg-orange-500/10 border border-orange-500/20 text-orange-600 dark:text-orange-400" title="স্ট্রিক">
                  <Flame className="w-3 h-3 text-orange-500 fill-orange-500 animate-pulse" />
                  <span>স্ট্রিক</span>
                </span>
              </div>
            </div>
          </div>

          {/* Social Action Button */}
          {!isSelf && (
            <div className="flex flex-col gap-2 w-full md:w-auto">
              <button
                onClick={handleSocialAction}
                disabled={socialLoading}
                className={`px-6 py-3 rounded-2xl font-bangla text-xs font-bold transition-all border-none cursor-pointer flex items-center justify-center gap-2 ${
                  socialLoading 
                    ? (isDarkMode ? 'bg-slate-800 text-gray-500' : 'bg-gray-100 text-gray-400')
                    : target.relation === 'friend'
                    ? 'bg-rose-500/10 hover:bg-rose-500/20 text-rose-500 border border-rose-500/20 shadow-sm'
                    : target.relation === 'pending_sent'
                    ? 'bg-rose-500/10 hover:bg-rose-500/20 text-rose-500 border border-rose-500/20 shadow-sm active:scale-[0.98]'
                    : target.relation === 'pending_received'
                    ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/20 active:scale-[0.98]'
                    : isDarkMode
                    ? 'bg-white hover:bg-gray-100 text-slate-900 shadow-md active:scale-[0.98]'
                    : 'bg-slate-900 hover:bg-slate-800 text-white shadow-md active:scale-[0.98]'
                }`}
                style={getButtonStyle()}
              >
                {socialLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : target.relation === 'friend' ? (
                  <>
                    <UserCheck className="w-4 h-4" />
                    <span>বন্ধুত্ব বাতিল করুন</span>
                  </>
                ) : target.relation === 'pending_sent' ? (
                  <>
                    <UserPlus className="w-4 h-4 text-rose-500 rotate-45" />
                    <span>অনুরোধ বাতিল করুন</span>
                  </>
                ) : target.relation === 'pending_received' ? (
                  <>
                    <UserPlus className="w-4 h-4" />
                    <span>অনুরোধ গ্রহণ করুন</span>
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4" />
                    <span>বন্ধু হিসেবে যোগ করো</span>
                  </>
                )}
              </button>

              {target.relation === 'friend' && onChallengeFriend && (
                <button
                  onClick={() => onChallengeFriend(target.id, targetName)}
                  className="px-6 py-3 rounded-2xl bg-[#00CBA9] hover:bg-[#00B097] text-white font-bangla text-xs font-bold border-none cursor-pointer flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20 active:scale-[0.98] transition-all"
                  style={{ backgroundColor: '#00CBA9', color: '#ffffff' }}
                >
                  <Play className="w-3.5 h-3.5 fill-current" />
                  <span>ব্যাটল আমন্ত্রণ পাঠান</span>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-6 border-t text-center ${
          isDarkMode ? 'border-white/5' : 'border-gray-100'
        }`}>
          <div className="space-y-0.5">
            <p className={`font-bangla text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-slate-400'}`}>পয়েন্ট</p>
            <p className={`font-bangla text-sm font-bold flex items-center justify-center gap-1 ${
              isDarkMode ? 'text-white' : 'text-slate-800'
            }`}>
              <Star className="w-4 h-4 text-amber-500 fill-current" />
              {toBnNum(displayPoints)}
            </p>
          </div>
          <div className="space-y-0.5">
            <p className={`font-bangla text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-slate-400'}`}>র‍্যাংক</p>
            <p className={`font-bangla text-sm font-bold flex items-center justify-center gap-1 ${
              isDarkMode ? 'text-white' : 'text-slate-800'
            }`}>
              <Award className="w-4 h-4 text-violet-400" />
              {toBnNum(displayRank)}
            </p>
          </div>
          <div className="space-y-0.5">
            <p className={`font-bangla text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-slate-400'}`}>পরীক্ষা</p>
            <p className={`font-bangla text-sm font-bold flex items-center justify-center gap-1 ${
              isDarkMode ? 'text-white' : 'text-slate-800'
            }`}>
              <Calendar className="w-4 h-4 text-blue-400" />
              {toBnNum(displayExams)}
            </p>
          </div>
          <div className="space-y-0.5">
            <p className={`font-bangla text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-slate-400'}`}>স্ট্রিক</p>
            <p className={`font-bangla text-sm font-bold flex items-center justify-center gap-1 ${
              isDarkMode ? 'text-white' : 'text-slate-800'
            }`}>
              <Flame className="w-4 h-4 text-orange-500 fill-orange-500 animate-pulse" />
              {toBnNum(displayStreak)}
            </p>
          </div>
        </div>
      </div>

      {/* Two Column details row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* 2. Comparison points chart card */}
        <div className="bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md rounded-3xl p-6 border border-gray-100 dark:border-white/5 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-bangla text-xs font-bold text-gray-700 dark:text-gray-300">
              {isSelf ? 'সাপ্তাহিক অর্জিত পয়েন্ট' : `You v/s ${targetName.split(' ')[0]}'s পয়েন্ট`}
            </h4>
            <div className="flex items-center gap-3 text-[10px] font-bangla font-semibold">
              {isSelf ? (
                <span className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span>পয়েন্ট - {toBnNum(shAuth?.user?.points || 0)}
                </span>
              ) : (
                <>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span>You - {toBnNum(shAuth?.user?.points || 0)}</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#00A3FF]"></span>{targetName.split(' ')[0]} - {toBnNum(target.points)}</span>
                </>
              )}
            </div>
          </div>

          {/* SVG line chart */}
          <div className="relative w-full h-[160px] flex items-center justify-center">
            <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="w-full h-full">
              {/* Horizontal grid lines */}
              {[0, 1, 2, 3].map((gridIdx) => {
                const yVal = padding + (gridIdx * (chartHeight - 2 * padding)) / 3;
                return (
                  <line 
                    key={gridIdx} 
                    x1={padding} 
                    y1={yVal} 
                    x2={chartWidth - padding} 
                    y2={yVal} 
                    stroke={isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'} 
                    strokeWidth="1" 
                  />
                );
              })}

              {/* Weekly labels */}
              {daysList.map((dayName: string, idx: number) => {
                const xVal = padding + (idx * (chartWidth - 2 * padding)) / 6;
                return (
                  <text 
                    key={idx} 
                    x={xVal} 
                    y={chartHeight - 2} 
                    textAnchor="middle" 
                    className="font-bangla text-[8px] fill-gray-400"
                  >
                    {dayName}
                  </text>
                );
              })}

              {/* Current User Line */}
              <path 
                d={getDPath(currentCoords)} 
                fill="none" 
                stroke="#F59E0B" 
                strokeWidth="2.5" 
                strokeLinecap="round" 
                className="opacity-70"
              />
              {currentCoords.map((coord, idx) => (
                <circle key={idx} cx={coord.x} cy={coord.y} r="3" fill="#F59E0B" />
              ))}

              {/* Target User Line (only if not self) */}
              {!isSelf && (
                <>
                  <path 
                    d={getDPath(targetCoords)} 
                    fill="none" 
                    stroke="#00A3FF" 
                    strokeWidth="3.5" 
                    strokeLinecap="round" 
                  />
                  {targetCoords.map((coord, idx) => (
                    <circle key={idx} cx={coord.x} cy={coord.y} r="4.5" fill="#00A3FF" className="stroke-white dark:stroke-zinc-900 stroke-2" />
                  ))}
                </>
              )}
            </svg>
          </div>
        </div>

        {/* 3. Subject wise Progress Report */}
        <div className="bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md rounded-3xl p-6 border border-gray-100 dark:border-white/5 shadow-sm space-y-4">
          <h4 className="font-bangla text-xs font-bold text-gray-700 dark:text-gray-300">সাবজেক্ট ভিত্তিক রিপোর্ট</h4>
          
          <div className="space-y-3.5 max-h-[320px] overflow-y-auto pr-1">
            {subjects.map((sub: any, idx: number) => {
              const isExpanded = expandedSubjectIdx === idx;
              return (
                <div key={idx} className="border-b border-gray-50 dark:border-white/5 last:border-0 pb-3 last:pb-0 space-y-2">
                  <div 
                    onClick={() => setExpandedSubjectIdx(isExpanded ? null : idx)}
                    className="flex items-center justify-between text-xs font-bangla font-bold text-[#1F1F1F] dark:text-gray-300 cursor-pointer"
                  >
                    <span>{sub.name}</span>
                    <span className="text-[#00CBA9]">{toBnNum(sub.target_progress)}%</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 bg-gray-100 dark:bg-white/5 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-[#00CBA9] to-[#00A3FF] rounded-full" 
                        style={{ width: `${sub.target_progress}%` }}
                      />
                    </div>
                    <button 
                      onClick={() => setExpandedSubjectIdx(isExpanded ? null : idx)}
                      className="p-1 rounded-lg hover:bg-slate-50 dark:hover:bg-white/5 border-none cursor-pointer bg-transparent text-gray-400"
                    >
                      {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="mt-2.5 space-y-3.5 p-4 rounded-2xl bg-slate-50/50 dark:bg-zinc-900/30 border border-slate-100 dark:border-white/5 animate-slideDown">
                      {/* Granular Chapter completion rates */}
                      <div className="space-y-2">
                        <h5 className="font-bangla text-[9px] font-bold text-gray-400 dark:text-zinc-550 uppercase tracking-wider">অধ্যায় ভিত্তিক অগ্রগতি</h5>
                        <div className="space-y-2">
                          {(() => {
                            const chaps = getChaptersForSubjectName(sub.name);
                            if (chaps && chaps.length > 0) {
                              return chaps.slice(0, 4).map((chap: any, cIdx: number) => {
                                const simulatedProgress = sub.target_progress > 0 
                                  ? Math.min(100, Math.max(0, Math.round(sub.target_progress * (1.1 - cIdx * 0.15))))
                                  : 0;
                                return (
                                  <div key={cIdx} className="flex items-center justify-between text-[10px] font-bangla font-semibold text-slate-650 dark:text-zinc-450">
                                    <span>{chap.name}</span>
                                    <span className="text-[#00CBA9]">{toBnNum(simulatedProgress)}%</span>
                                  </div>
                                );
                              });
                            } else {
                              return (
                                <div className="text-[10px] font-bangla text-gray-400 italic">
                                  কোনো অধ্যায় পাওয়া যায়নি
                                </div>
                              );
                            }
                          })()}
                        </div>
                      </div>

                      <div className="border-t border-slate-100 dark:border-white/5 my-1.5" />

                      <div className="flex items-center justify-around text-[10px] font-bangla font-semibold text-slate-650 dark:text-slate-400">
                        <span className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-[#10b981]"></span>
                          {toBnNum(11)} সঠিক
                        </span>
                        <span className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-[#ef4444]"></span>
                          {toBnNum(14)} ভুল
                        </span>
                        <span className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-[#f59e0b]"></span>
                          {toBnNum(0)} স্কিপড
                        </span>
                      </div>
                      <button 
                        onClick={() => setSelectedSubjectDetail(sub)}
                        className="w-full py-2.5 bg-white hover:bg-slate-50 dark:bg-white/5 dark:hover:bg-white/10 text-slate-700 dark:text-zinc-350 font-bangla text-[10px] font-extrabold rounded-xl border border-slate-200/50 dark:border-white/5 cursor-pointer transition-colors text-center"
                      >
                        বিস্তারিত রিপোর্ট দেখো ➡️
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

      </div>

      {/* 4. Badges Card */}
      <div className="bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md rounded-3xl p-6 border border-gray-100 dark:border-white/5 shadow-sm space-y-4">
        <h4 className="font-bangla text-xs font-bold text-gray-700 dark:text-gray-300">ব্যাজেস</h4>
        
        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="p-5 rounded-3xl bg-gray-50 dark:bg-white/3 flex flex-col items-center justify-center space-y-2.5 border border-gray-100 dark:border-white/5 hover:border-[#00CBA9]/30 transition-all duration-300">
            <div className="w-12 h-12 rounded-full bg-[#00CBA9]/10 border border-[#00CBA9]/20 flex items-center justify-center text-[#00CBA9]">
              <Star className="w-6 h-6 text-amber-500 fill-amber-500" />
            </div>
            <p className="font-bangla text-xs font-extrabold text-[#1F1F1F] dark:text-white">সঠিক উত্তর</p>
            <p className="font-bangla text-[10px] text-gray-500">{toBnNum(displayPoints)} পয়েন্ট</p>
          </div>

          <div className="p-5 rounded-3xl bg-gray-50 dark:bg-white/3 flex flex-col items-center justify-center space-y-2.5 border border-gray-100 dark:border-white/5 hover:border-emerald-400/30 transition-all duration-300">
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
              <BookOpen className="w-6 h-6" />
            </div>
            <p className="font-bangla text-xs font-extrabold text-[#1F1F1F] dark:text-white">মোট পরীক্ষা</p>
            <p className="font-bangla text-[10px] text-gray-500">{toBnNum(displayExams)}টি পরীক্ষা সম্পন্ন</p>
          </div>

          <div className="p-5 rounded-3xl bg-gray-50 dark:bg-white/3 flex flex-col items-center justify-center space-y-2.5 border border-gray-100 dark:border-white/5 hover:border-orange-500/30 transition-all duration-300">
            <div className="w-12 h-12 rounded-full bg-orange-500/10 border border-orange-500/20 flex items-center justify-center text-orange-500">
              <Flame className="w-6 h-6 fill-orange-500 text-orange-500 animate-pulse" />
            </div>
            <p className="font-bangla text-xs font-extrabold text-[#1F1F1F] dark:text-white">স্ট্রিক</p>
            <p className="font-bangla text-[10px] text-gray-500">{toBnNum(displayStreak)} দিন স্ট্রিক</p>
          </div>
        </div>
      </div>

    </div>
  );
}
